import React from "react";
import { ChevronLeft } from "lucide-react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { NavHistory } from "@/lib/nav-history";

export default function TopBar({
  title,
  subtitle,
  subtitleTo,
  backTo,
  onBack,
  safeFallback,
  right,
  testId = "top-bar",
}) {
  const nav      = useNavigate();
  const location = useLocation();
  const params   = useParams();

  const handleBack = () => {
    // 1. Custom handler — full control
    if (onBack) return onBack();

    // 2. Explicit destination prop — override everything
    if (backTo) return nav(backTo, { replace: true });

    // 3. Cold start: page opened directly (deep link, bookmark, refresh)
    //    No real in-app history exists — apply context heuristics to find
    //    the most logical parent without guessing.
    if (location.key === "default") {
      const path = location.pathname;

      // Person nested under a family context
      if (path.startsWith("/people") && params.familyId) {
        return nav(`/families/${params.familyId}`, { replace: true });
      }

      // Church sub-page → parent church detail
      if (path.startsWith("/churches/") && params.id && path !== `/churches/${params.id}`) {
        return nav(`/churches/${params.id}`, { replace: true });
      }

      // Generic fallback to declared safe destination or root
      return nav(safeFallback || "/", { replace: true });
    }

    // 4. Real in-app history — use NavHistory which skips form routes
    //    automatically (create / edit / wizard / setup / onboarding).
    const target = NavHistory.back(safeFallback || "/");
    nav(target, { replace: true });
  };

  return (
    <div
      className="
        border-b border-slate-200/50 dark:border-slate-800/50
        bg-white dark:bg-slate-900
      "
      data-testid={testId}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">

        {/* BACK BUTTON */}
        <button
          onClick={handleBack}
          aria-label="Regresar"
          className="
            h-11 w-11 shrink-0
            flex items-center justify-center
            rounded-full
            border border-slate-200 dark:border-slate-700
            bg-white dark:bg-slate-800
            shadow-sm
            text-slate-600 dark:text-slate-300
            transition-all duration-200 ease-out
            hover:shadow-md hover:text-slate-800 dark:hover:text-slate-100
            active:scale-[0.98]
          "
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
        </button>

        {/* TITLE */}
        <div className="flex-1 min-w-0">
          <h1 className="text-[16px] font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate leading-tight">
            {title}
          </h1>
          {subtitle && (
            subtitleTo
              ? (
                <Link
                  to={subtitleTo}
                  className="text-[11px] tracking-wide text-slate-400 dark:text-slate-500 truncate mt-0.5 leading-none hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150 block"
                >
                  {subtitle}
                </Link>
              )
              : (
                <p className="text-[11px] tracking-wide text-slate-400 dark:text-slate-500 truncate mt-0.5 leading-none">
                  {subtitle}
                </p>
              )
          )}
        </div>

        {/* CONTEXTUAL ACTIONS */}
        {right && (
          <div className="flex items-center gap-2 shrink-0">
            {right}
          </div>
        )}

      </div>
    </div>
  );
}
